import React from 'react';
import { Target, Calendar, Users, TrendingUp, MapPin, AlertCircle } from 'lucide-react';

/**
 * IntentFilter Component
 *
 * Allows users to filter the map by visit intent/reason.
 * Supports both planned visits (intents) and CRM statuses.
 */
const IntentFilter = ({ selectedIntents, onIntentChange }) => {
    const intentOptions = [
        {
            id: 'all',
            label: 'All Visits',
            icon: MapPin,
            color: 'gray',
            description: 'Show all planned visits'
        },
        {
            id: 'manual_planning',
            label: 'Manual Planning',
            icon: Target,
            color: 'blue',
            description: 'User-added visits'
        },
        {
            id: 'scheduled_follow_up',
            label: 'Scheduled Follow-up',
            icon: Calendar,
            color: 'purple',
            description: 'Recurring scheduled visits'
        },
        {
            id: 'ai_recommended',
            label: 'AI Recommended',
            icon: TrendingUp,
            color: 'emerald',
            description: 'AI-suggested opportunities'
        },
        {
            id: 'relationship_building',
            label: 'Relationship Building',
            icon: Users,
            color: 'indigo',
            description: 'Proactive customer visits'
        },
        {
            id: 'at_risk_intervention',
            label: 'At-Risk Intervention',
            icon: AlertCircle,
            color: 'orange',
            description: 'Urgent customer retention'
        }
    ];

    const handleToggle = (intentId) => {
        if (intentId === 'all') {
            // Selecting "All" clears other filters
            onIntentChange(['all']);
        } else {
            let newSelection;
            if (selectedIntents.includes('all')) {
                // If "All" was selected, replace with the clicked intent
                newSelection = [intentId];
            } else if (selectedIntents.includes(intentId)) {
                // Deselect the intent
                newSelection = selectedIntents.filter(id => id !== intentId);
                // If nothing selected, default to "all"
                if (newSelection.length === 0) {
                    newSelection = ['all'];
                }
            } else {
                // Add the intent to selection
                newSelection = [...selectedIntents, intentId];
            }
            onIntentChange(newSelection);
        }
    };

    const getColorClasses = (color, isSelected) => {
        const baseClasses = 'transition-all';

        if (isSelected) {
            return {
                gray: `bg-gray-600 text-white border-gray-700 ${baseClasses}`,
                blue: `bg-blue-600 text-white border-blue-700 ${baseClasses}`,
                purple: `bg-purple-600 text-white border-purple-700 ${baseClasses}`,
                emerald: `bg-emerald-600 text-white border-emerald-700 ${baseClasses}`,
                indigo: `bg-indigo-600 text-white border-indigo-700 ${baseClasses}`,
                orange: `bg-orange-600 text-white border-orange-700 ${baseClasses}`
            }[color];
        } else {
            return {
                gray: `bg-white text-gray-700 border-gray-300 hover:bg-gray-50 ${baseClasses}`,
                blue: `bg-white text-blue-700 border-blue-300 hover:bg-blue-50 ${baseClasses}`,
                purple: `bg-white text-purple-700 border-purple-300 hover:bg-purple-50 ${baseClasses}`,
                emerald: `bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50 ${baseClasses}`,
                indigo: `bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50 ${baseClasses}`,
                orange: `bg-white text-orange-700 border-orange-300 hover:bg-orange-50 ${baseClasses}`
            }[color];
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
                <Target className="w-5 h-5 text-indigo-600" />
                <h3 className="font-bold text-gray-900">Filter by Visit Intent</h3>
            </div>

            <div className="space-y-2">
                {intentOptions.map(option => {
                    const Icon = option.icon;
                    const isSelected = selectedIntents.includes(option.id);

                    return (
                        <button
                            key={option.id}
                            onClick={() => handleToggle(option.id)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 ${getColorClasses(option.color, isSelected)}`}
                            title={option.description}
                        >
                            <Icon className="w-5 h-5 flex-shrink-0" />
                            <div className="flex-1 text-left">
                                <div className="font-semibold text-sm">{option.label}</div>
                                <div className={`text-xs ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                                    {option.description}
                                </div>
                            </div>
                            {isSelected && (
                                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
                                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                    {selectedIntents.includes('all') ? (
                        <span className="font-medium">Showing all planned visits</span>
                    ) : (
                        <span className="font-medium">{selectedIntents.length} intent{selectedIntents.length !== 1 ? 's' : ''} selected</span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IntentFilter;
